/**
 * Security audit trail (client side).
 *
 * Records security-relevant events — failed sign-ins, permission denials,
 * blocked admin navigation — into `public.security_audit_log`. Admins read
 * them at /admin/audit-log.
 *
 * Rules:
 *  - Fire and forget. A failed audit write must never break a user flow.
 *  - Only the allowlisted `event_type` values below are accepted by RLS.
 *  - Never record passwords, tokens, or full request bodies.
 */
import { supabase } from "@/integrations/supabase/client";

export type AuditCategory = "auth" | "permission" | "email_queue" | "admin" | "data" | "other";
export type AuditSeverity = "info" | "warning" | "critical";

/** Event types the database policy accepts from a browser client. */
export const CLIENT_AUDIT_EVENTS = {
  signInFailed: "auth.sign_in_failed",
  signUpFailed: "auth.sign_up_failed",
  passwordResetRequested: "auth.password_reset_requested",
  passwordResetFailed: "auth.password_reset_failed",
  sessionExpired: "auth.session_expired",
  permissionDenied: "permission.denied",
  adminRouteBlocked: "admin.route_blocked",
} as const;

export type ClientAuditEvent = (typeof CLIENT_AUDIT_EVENTS)[keyof typeof CLIENT_AUDIT_EVENTS];

const CATEGORY_BY_EVENT: Record<ClientAuditEvent, AuditCategory> = {
  "auth.sign_in_failed": "auth",
  "auth.sign_up_failed": "auth",
  "auth.password_reset_requested": "auth",
  "auth.password_reset_failed": "auth",
  "auth.session_expired": "auth",
  "permission.denied": "permission",
  "admin.route_blocked": "admin",
};

interface RecordOptions {
  severity?: AuditSeverity;
  /** One-line, human-readable description. Truncated to 300 characters. */
  summary?: string;
  /** Email typed into a form; stored so repeated attempts can be grouped. */
  actorEmail?: string | null;
  metadata?: Record<string, unknown>;
}

/** Simple in-memory throttle so a retry loop cannot flood the log. */
const lastSent = new Map<string, number>();
const THROTTLE_MS = 5_000;

export async function recordSecurityEvent(
  eventType: ClientAuditEvent,
  options: RecordOptions = {},
): Promise<void> {
  const key = `${eventType}|${options.actorEmail ?? ""}|${options.summary ?? ""}`;
  const now = Date.now();
  const previous = lastSent.get(key);
  if (previous && now - previous < THROTTLE_MS) return;
  lastSent.set(key, now);

  try {
    const { data } = await supabase.auth.getSession();
    const userId = data.session?.user?.id ?? null;

    await supabase.from("security_audit_log").insert([{
      event_type: eventType,
      category: CATEGORY_BY_EVENT[eventType],
      severity: options.severity ?? "warning",
      source: "client",
      actor_user_id: userId,
      actor_email: options.actorEmail?.trim().toLowerCase() || data.session?.user?.email || null,
      route: typeof window !== "undefined" ? window.location.pathname : null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 300) : null,
      summary: options.summary?.slice(0, 300) ?? null,
      metadata: (options.metadata ?? {}) as never,
    }]);
  } catch {
    // Auditing is best effort; never surface or rethrow.
  }
}
